import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, PageShell, Eyebrow, EASE } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { usePageTitle } from "@/hooks/usePageTitle";

const FILTERS = ["All", "Completed", "Ongoing"];

export default function Projects() {
  usePageTitle("Projects — Bungalows, Farmhouses & Villas | Sai Leela Decor");
  const [filter, setFilter] = useState("All");
  const c = useContent();
  const PROJECTS = c.projects;
  const visible = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.status === filter);

  return (
    <PageShell testid="projects-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-16">
        <FadeUp>
          <Eyebrow className="mb-5">Portfolio</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08] text-bone max-w-3xl">
            Built <span className="italic text-bronze">works</span>
          </h1>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-stone2">
            Turnkey bungalows, farmhouses and villas — designed, constructed and
            finished by Sai Leela Decor across Maharashtra, from plot to possession.
          </p>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-12 flex flex-wrap gap-3">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}-btn`}
              onClick={() => setFilter(f)}
              className={`text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 border transition-all duration-500 ${
                filter === f
                  ? "border-bronze bg-bronze text-obsidian"
                  : "border-white/15 text-bone/60 hover:border-bronze/50 hover:text-bone"
              }`}
            >
              {f}
            </button>
          ))}
        </FadeUp>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.6, delay: 0.05 * i, ease: EASE }}
                              >
                <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`} className="group block">
                  <div className="relative overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-auto transition-transform duration-700 ease-luxury group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
                    <span className="absolute top-5 left-5 font-mono text-[10px] tracking-widest text-bone/70 bg-obsidian/50 backdrop-blur px-3 py-1.5 border border-white/10">
                      {String(PROJECTS.indexOf(p) + 1).padStart(2, "0")}
                    </span>
                    <span className="absolute top-5 right-5 font-mono text-[10px] tracking-widest text-bronze bg-obsidian/50 backdrop-blur px-3 py-1.5 border border-bronze/30">
                      {p.location}
                    </span>
                    <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8 flex items-end justify-between gap-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-2">{p.category} · {p.status}</p>
                        <h2 className="font-serif text-xl sm:text-2xl lg:text-3xl font-light text-bone">{p.title}</h2>
                      </div>
                      <span className="w-11 h-11 shrink-0 border border-bone/30 flex items-center justify-center text-bone transition-all duration-500 group-hover:bg-bronze group-hover:border-bronze group-hover:text-obsidian">
                        <ArrowUpRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>
    </PageShell>
  );
}
